import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import BoxWidget from '@/components/box-widget/BoxWidget.vue';
import StatBox from '@/components/campaign-statistics/stat-box/StatBox.vue';
import { initDycolor, moment, helper } from '~';
import { GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_REACH_FREQUENCY, GOAL_POST_ENGAGEMENT, MODES } from '@/util/constants';

initDycolor('campaign-statistics', require(`${process.env.DYCOLOR_REQUIRE}./CampaignStatistics.scss`)); // eslint-disable-line

export default {
  components: {
    'box-widget': BoxWidget,
    'stat-box': StatBox,
  },
  data() {
    return {
      isShownCalendar: false,
      year: null,
      iterationsSelected: [],
      itemClass: 'col-md-4',
    };
  },
  computed: {
    ...mapState('genericConfig', ['config']),
    ...mapGetters('campaign', {
      campaign: 'currentCampaign',
      currentIterationRaw: 'currentIteration',
    }),
    isOneOff() {
      if (_.get(this.campaign, 'mode', '') === MODES.oneoff) {
        return true;
      }
      return false;
    },
    iterationsGroupedByYearAndMonth() {
      if (!this.campaign.iterations || this.campaign.iterations.length === 0) {
        return {};
      }
      const result = {};
      this.campaign.iterations.forEach((iteration) => {
        const date = moment(iteration.start_date);
        let curYear = date.year();
        if (isNaN(curYear)) {
          curYear = moment().year();
        }
        if (typeof result[curYear] === 'undefined') {
          result[curYear] = {};
        }
        result[curYear][date.month()] = iteration;
      });
      return result;
    },
    totalIterations() {
      const result = {
        stats: this.campaign.stats,
        start_date: this.campaign.start_date,
        finish_date: this.campaign.end_date,
      };

      return this.viewIteration(result);
    },
    currentIteration() {
      if (this.currentIterationRaw) {
        return this.viewIteration(this.currentIterationRaw);
      }
      return null;
    },
    selectedIterations() {
      const result = [];
      this.iterationsSelected.forEach((pointer) => {
        const iteration = this.iterationsGroupedByYearAndMonth[pointer.year][pointer.month];
        const viewIteration = this.viewIteration(iteration);
        result.push(viewIteration);
      });

      return result;
    },
    calendarMonths() {
      const result = [];
      const date = moment();
      for (let month = 0; month < 12; month += 1) {
        date.month(month);
        result.push({
          monthName: date.format(this.$t('format.time.month-short')),
          hasIteration: this.hasIteration(this.selectedYear, month),
          year: this.selectedYear.toString().slice(2, 4),
        });
      }
      return result;
    },
    prevYear() {
      return this.selectedYear - 1;
    },
    nextYear() {
      return this.selectedYear + 1;
    },
    selectedYear() {
      if (this.year === null) {
        let keys = Object.keys(this.iterationsGroupedByYearAndMonth);
        keys = keys.sort();
        if (keys.length > 0) {
          this.year = keys[keys.length - 1];
        } else {
          this.year = moment().year();
        }
      }
      if (this.year !== null) {
        return parseInt(this.year, 10);
      }
      return this.year;
    },
    advancedStats() {
      return _.get(this.config, 'feature_use_advanced_statistics', null);
    },
  },
  methods: {
    onToggleCalendar() {
      this.isShownCalendar = !this.isShownCalendar;
    },
    onMonthClicked(year, month) {
      const selected = this.iterationsSelected.find(it => it.year === year && it.month === month);
      if (this.hasIteration(year, month) && !selected) {
        this.iterationsSelected.push({ year, month });
      }
    },
    onRemoveSelectedIteration(index) {
      delete this.iterationsSelected.splice(index, 1);
    },
    onChangeYear(year) {
      this.year = year;
    },
    isVideoType() {
      return this.campaign.type === 'video';
    },
    isLeadAd() {
      return this.campaign.campaign_rule.goal_type === GOAL_LEAD_GENERATION;
    },
    isOfferAd() {
      return this.campaign.campaign_rule.goal_type === GOAL_LOCAL_OFFERS;
    },
    isFrequencyAd() {
      return this.campaign.campaign_rule.goal_type === GOAL_REACH_FREQUENCY
        || this.campaign.campaign_rule.goal_type === GOAL_POST_ENGAGEMENT;
    },
    getEnganementRate(stats) {
      if (stats && stats.reach > 0) {
        return parseFloat((stats.clicks / stats.reach) * 100).toFixed(2);
      }
      return 0;
    },
    stats(iteration, overall) {
      const stats = (typeof iteration.stats === 'string') ? JSON.parse(iteration.stats) : iteration.stats;

      const allFields = {
        reach: {
          title: this.$t('terms.stats.reach'),
          icon: 'people-reached',
          value: _.get(stats, 'reach', 0),
          details: null,
        },
        frequency: {
          title: this.$t('terms.stats.frequency'),
          icon: 'frequency',
          value: _.get(stats, 'frequency', 0),
          details: null,
        },
        clicks: {
          title: this.$t('terms.stats.clicks'),
          icon: 'clicks',
          value: _.get(stats, 'clicks', 0),
          details: null,
        },
        video_view: {
          title: this.$t('terms.stats.video_view'),
          icon: 'video',
          value: _.get(stats, 'video_view', 0),
          details: null,
        },
        leadgen_other: {
          title: this.$t('terms.stats.leadgen_other'),
          icon: 'leadgen_other',
          value: _.get(stats, 'leadgen_other', 0),
          details: null,
        },
        engagement_rate: {
          title: this.$t('terms.stats.engagement_rate'),
          icon: 'engagement_rate',
          value: this.getEnganementRate(stats),
          details: null,
        },
        receive_offer: {
          title: this.$t('terms.stats.receive_offer'),
          icon: 'receive_offer',
          value: _.get(stats, 'receive_offer', 0),
          details: null,
        },
        unique_clicks: {
          title: this.$t('terms.stats.unique_clicks'),
          icon: 'unique_clicks',
          value: _.get(stats, 'unique_clicks', 0),
          details: null,
        },
        post_engagement: {
          title: this.$t('terms.stats.post_engagement'),
          icon: 'post_engagement',
          value: _.get(stats, 'post_engagement', 0),
          details: helper.getCampaignStatsDetails(
            'post_engagement', stats, this.campaign.campaign_rule.goal_type, this.campaign.type),
        },
        page_engagement: {
          title: this.$t('terms.stats.page_engagement'),
          icon: 'page_engagement',
          value: _.get(stats, 'page_engagement', 0),
          details: null,
        },
        impressions: {
          title: this.$t('terms.stats.impressions'),
          icon: 'impressions',
          value: _.get(stats, 'impressions', 0),
          details: null,
        },
        outbound_clicks: {
          title: this.$t('terms.stats.outbound_clicks'),
          icon: 'outbound_clicks',
          value: _.get(stats, 'outbound_clicks', 0),
          details: null,
        },
        total_clicks: {
          title: this.$t('terms.stats.total_clicks'),
          icon: 'total_clicks',
          value: _.get(stats, 'clicks', 0),
          details: helper.getCampaignStatsDetails(
            'total_clicks', stats, this.campaign.campaign_rule.goal_type, this.campaign.type),
        },
        landing_clicks: {
          title: _.get(this, 'campaign.campaign_rule.goal_type', '') === GOAL_LEAD_GENERATION
            ? this.$t('terms.stats.form_openings')
            : this.$t('terms.stats.landing_clicks'),
          icon: 'clicks',
          value: _.get(stats, 'link_clicks', 0),
          details: null,
        },
      };

      const usedFields = helper.getDisplayedStatsKeys(
        this.campaign.campaign_rule.goal_type,
        this.advancedStats,
        overall,
      );
      if (this.isVideoType()) {
        usedFields.push('video_view');
      }

      const result = [];
      usedFields.forEach(fieldName => {
        if (allFields[fieldName]) {
          result.push(allFields[fieldName]);
        }
      });

      this.calculateItemClass(usedFields.length);

      return result;
    },
    calculateItemClass(count) {
      if (this.advancedStats) {
        this.itemClass = 'col-md-3';
      } else if (count % 4 === 0) {
        this.itemClass = 'col-md-3';
      } else if (count % 3 === 0) {
        this.itemClass = 'col-md-4';
      } else if (count === 2) {
        this.itemClass = 'col-md-6';
      } else if (count === 1) {
        this.itemClass = 'col-md-12';
      // } else {
      //   this.itemClass = 'col-md-3';
      }
    },
    viewIteration(iteration) {
      // deep clone object
      const result = JSON.parse(JSON.stringify(iteration));
      const date = moment(result.start_date);
      result.stats = (typeof iteration.stats === 'string') ? JSON.parse(result.stats) : result.stats;
      if (date.isValid()) {
        result.startDate = this.$t('fn.date', { timestamp: iteration.start_date });
      } else {
        result.startDate = '';
      }
      result.endDate = result.finish_date === '0000-00-00 00:00:00' || result.finish_date === null
          ? this.$t('pos.campaign.iterations.now')
          : this.$t('fn.date', { timestamp: result.finish_date });
      result.shortMonthName = date.format(this.$t('format.time.month-short'));
      result.year = date.format('YYYY');
      return result;
    },
    hasIteration(year, month = null) {
      const data = this.iterationsGroupedByYearAndMonth;
      if (month === null) {
        return typeof data[year] !== 'undefined';
      }
      return typeof data[year] !== 'undefined' && typeof data[year][month] !== 'undefined';
    },
    statValue(key, value) {
      if (key === 'engagement_rate') {
        return `${value}%`;
      }
      if (key === 'frequency') {
        return value.toFixed(1);
      }
      return this.$t('fn.number', { value });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-statistics/CampaignStatistics.js